import * as S from './LicenseAdminPage.styles';

import React, {useMemo, useState} from 'react';
import {CellInfo, Column} from 'react-table';
import {Header, Button, Modal, Form, List, Message} from 'semantic-ui-react';

import * as FuzzyMatch from '../util/fuzzyMatch';
import WBReactTable, {DataRow} from '../components/WBReactTable';
import WandbLoader from '../components/WandbLoader';
import NoMatch from '../components/NoMatch';
import {useViewer} from '../state/viewer/hooks';
import CopyableText from '../components/CopyableText';
import {camelize} from '../util/text';
import EditableField from '../components/EditableField';

import {
  Exact,
  useLocalLicensesQuery,
  useGenerateLocalLicenseMutation,
  useUpdateLocalLicenseMutation,
  LocalLicensesQuery,
} from '../generated/graphql';
import {
  propagateErrorsContext,
  extractErrorMessageFromApolloError,
} from '../util/errors';
import LegacyWBIcon from '../components/elements/LegacyWBIcon';
import moment from 'moment';
import {QueryResult} from 'react-apollo';
import _ from 'lodash';

type fecthedLicenseType = QueryResult<
  LocalLicensesQuery,
  Exact<{
    expired?: boolean | null | undefined;
  }>
>;

const searchedColumnNames: string[] = ['UID', 'Customer Name', 'Email'];
const nonsearchedColumnNames: string[] = [
  'License',
  'Notes',
  'Generated By',
  'Expires At',
  'Storage Gigs',
  'Teams',
  'Seats',
  'Flags',
];

const smallColumns = ['Storage Gigs', 'Teams', 'Seats', 'Expires At'];
const mediumColumns = ['Generated By', 'License', 'Notes'];

const LicenseAdminPage: React.FC = () => {
  const validLocalLicenses = useLocalLicensesQuery({
    variables: {
      expired: false,
    },
    fetchPolicy: 'network-only',
  });
  const expiredLocalLicenses = useLocalLicensesQuery({
    variables: {
      expired: true,
    },
    fetchPolicy: 'network-only',
  });
  const [showModal, setShowModal] = useState<boolean>(false);
  const [updateLicense] = useUpdateLocalLicenseMutation();

  const viewer = useViewer();

  // fetch valid or expired local licenses
  const parseLicenseToRow = (license: fecthedLicenseType): DataRow[] => {
    return license.data
      ? license.data.localLicenses.map(l => {
          if (l == null) {
            return {searchString: '', row: {}};
          }
          const claims = l.claims;
          return {
            searchString: l.uid + '|' + l.customerName + '|' + l.contactEmail,
            row: {
              id: l.id,
              uid: l.uid,
              license: l.license,
              customerName: l.customerName,
              email: l.contactEmail,
              generatedBy: l.generatedBy.username,
              expiresAt: moment.unix(claims.expiresAt).format('MM/DD/YYYY'),
              teams: claims.teams,
              seats: claims.seats,
              storageGigs: claims.storageGigs,
              flags: claims.flags,
              notes: l.notes,
            },
          };
        })
      : [];
  };

  const validLicenseRows: DataRow[] = parseLicenseToRow(validLocalLicenses);
  const expiredLicenseRows: DataRow[] = parseLicenseToRow(expiredLocalLicenses);

  const toBeSearchedColumns: Column[] = searchedColumnNames.map(name => {
    return {
      Header: name,
      accessor: camelize(name),
      Cell: (cellInfo: CellInfo) => {
        if (name === 'Customer Name') {
          return <span>{cellInfo.value}</span>;
        }
        return (
          <S.OnelineCell>
            <CopyableText
              text={cellInfo.value || ''}
              toastText={`Copied ${name} to clipboard.`}
            />
          </S.OnelineCell>
        );
      },
    };
  });

  // use memo to prevent remounting in WBReactTable
  const nonsearchedColumns: Column[] = useMemo(
    () =>
      nonsearchedColumnNames.map(name => {
        return {
          Header: name === 'Storage Gigs' ? 'Storage (G)' : name,
          accessor: camelize(name),
          width: smallColumns.includes(name)
            ? 100
            : mediumColumns.includes(name)
            ? 128
            : 152,
          Cell: (cellInfo: CellInfo) => {
            if (name === 'Flags') {
              const flagString: string = _.isArray(cellInfo.value)
                ? cellInfo.value.join(', ')
                : '';
              return <span>{flagString}</span>;
            } else if (name === 'License') {
              return (
                <S.OnelineCell>
                  <CopyableText
                    text={cellInfo.value || ''}
                    toastText="Copied license to clipboard."
                  />
                </S.OnelineCell>
              );
            } else if (name === 'Notes') {
              return (
                <EditableField
                  value={cellInfo.value || ''}
                  placeholder={''}
                  updateValue
                  multiline
                  showEditIcon
                  save={text => {
                    updateLicense({
                      variables: {id: cellInfo.original.id, notes: text},
                    });
                  }}
                />
              );
            }
            return <S.CenteredCell>{cellInfo.value}</S.CenteredCell>;
          },
        };
      }),
    [updateLicense]
  );
  const columns = [...toBeSearchedColumns, ...nonsearchedColumns];

  const searchResultColumns = (searchQuery: string) => {
    const searchedColumns: Column[] = searchedColumnNames.map(name => {
      return {
        Header: name,
        accessor: camelize(name),
        Cell: (cellInfo: CellInfo) => (
          <S.OnelineCell>
            {FuzzyMatch.fuzzyMatchHighlight(cellInfo.value, searchQuery)}
          </S.OnelineCell>
        ),
      };
    });
    return [...searchedColumns, ...nonsearchedColumns];
  };

  const openModalHandler = () => {
    setShowModal(true);
  };
  const closeModalHandler = () => {
    setShowModal(false);
  };
  const submitFormHandler = () => {
    setShowModal(false);
    validLocalLicenses.refetch();
    expiredLocalLicenses.refetch();
  };

  // no case for undefined viewer because this is wrapped in privateRoute
  if (viewer && !viewer.admin) {
    return <NoMatch />;
  }
  if (validLocalLicenses.loading || expiredLocalLicenses.loading) {
    return <WandbLoader />;
  }

  return (
    <S.Wrapper>
      <S.HeaderWrapper>
        <Header as="h1">Manage Local Licenses</Header>
        <S.GenerateButtonWrapper>
          <Button primary size="small" onClick={openModalHandler}>
            Generate License
          </Button>
        </S.GenerateButtonWrapper>
        <Modal
          size="tiny"
          as={Form}
          open={showModal}
          onClose={closeModalHandler}>
          <GenerateLicenseForm
            onSubmitForm={submitFormHandler}
            onCloseForm={closeModalHandler}
          />
        </Modal>
      </S.HeaderWrapper>
      <S.Divider />
      <Header as="h2">Valid Local Licenses</Header>
      <WBReactTable
        data={validLicenseRows}
        columns={columns}
        sortable={true}
        pageSize={10}
        searchResultColumns={searchResultColumns}
      />
      <Header as="h2">Expired Local Licenses</Header>
      <WBReactTable
        data={expiredLicenseRows}
        columns={columns}
        sortable={true}
        pageSize={10}
        searchResultColumns={searchResultColumns}
      />
    </S.Wrapper>
  );
};

interface GenerateLicenseFormProps {
  onSubmitForm(): void;
  onCloseForm(): void;
}

const GenerateLicenseForm: React.FC<GenerateLicenseFormProps> = props => {
  const [customerName, setCustomerName] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [teams, setTeams] = useState<number>(0);
  const [seats, setSeats] = useState<number>(4);
  const [storageGigs, setStorageGigs] = useState<number>(2);
  const [expDuration, setExpDuration] = useState<number>(365);
  const [flags, setFlags] = useState(['auth0', 'mysql', 's3']);
  const [newFlag, setNewFlag] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const [generateLicense] = useGenerateLocalLicenseMutation({
    context: propagateErrorsContext(),
  });

  const onSubmitHandler = async () => {
    try {
      const generateLicenseResult = await generateLicense({
        variables: {
          customerName,
          contactEmail: email,
          notes,
          teams,
          seats,
          expDuration,
          storageGigs,
          flags,
        },
      });
      if (generateLicenseResult.errors != null) {
        setError(
          `Error generating license: ${generateLicenseResult.errors.join(', ')}`
        );
        return;
      }
    } catch (err) {
      const errMsg = extractErrorMessageFromApolloError(err) ?? err.message;
      setError(`Error generating license: ${errMsg}`);
    } finally {
      props.onSubmitForm();
    }
  };

  const addNewFlagHandler = () => {
    if (flags.includes(newFlag)) {
      setError(`New flag ${newFlag} already exists`);
    } else {
      setFlags([newFlag, ...flags]);
      setNewFlag('');
    }
  };

  const setValueWithDefault = (
    setValueFunc: React.Dispatch<React.SetStateAction<number>>,
    value: string | number,
    defaultValue: number
  ): void => {
    if (typeof value === 'string') {
      value = Number(value);
    }
    isNaN(value) ? setValueFunc(defaultValue) : setValueFunc(value);
  };

  return (
    <>
      <Modal.Header>Generate a new license</Modal.Header>
      {error && <Message negative content={error} />}
      <Modal.Content>
        <S.InputHeader>Company Name</S.InputHeader>
        <Form.Input
          value={customerName}
          onChange={(e, {value}) => {
            setCustomerName(value);
          }}
        />
        <S.InputHeader>Email</S.InputHeader>
        <Form.Input
          value={email}
          onChange={(e, {value}) => {
            setEmail(value);
          }}
        />
        <S.InputHeader>Notes</S.InputHeader>
        <Form.Input
          value={notes}
          onChange={(e, {value}) => {
            setNotes(value);
          }}
        />
        <S.ClaimsWrapper>
          <S.FormColumn>
            <S.InputRow>
              <S.InputLabel>Teams</S.InputLabel>
              <Form.Input
                value={teams}
                width={7}
                onChange={(e, {value}) => {
                  setValueWithDefault(setTeams, value, 0);
                }}
              />
            </S.InputRow>
            <S.InputRow>
              <S.InputLabel>Seats</S.InputLabel>
              <Form.Input
                value={seats}
                width={7}
                onChange={(e, {value}) => {
                  setValueWithDefault(setSeats, value, 0);
                }}
              />
            </S.InputRow>
            <S.InputRow>
              <S.InputLabel>Storage GB</S.InputLabel>
              <Form.Input
                value={storageGigs}
                width={7}
                onChange={(e, {value}) => {
                  setValueWithDefault(setStorageGigs, value, 0);
                }}
              />
            </S.InputRow>
            <S.InputRow>
              <S.InputLabel>Days until expiration</S.InputLabel>
              <Form.Input
                value={expDuration}
                width={7}
                onChange={(e, {value}) => {
                  setValueWithDefault(setExpDuration, value, 365);
                }}
              />
            </S.InputRow>
          </S.FormColumn>
          <S.FormColumn>
            <S.InputHeader>Flags</S.InputHeader>
            <S.NewFlagInputWrapper>
              <S.InputWrapper>
                <Form.Input
                  style={{height: '39px'}}
                  value={newFlag}
                  placeholder="New Flag"
                  width="16"
                  onChange={(e, {value}) => {
                    setNewFlag(value);
                  }}
                />
              </S.InputWrapper>
              {/* <S.AddButtonWrapper> */}
              <Button disabled={newFlag === ''} onClick={addNewFlagHandler}>
                Add
              </Button>
              {/* </S.AddButtonWrapper> */}
            </S.NewFlagInputWrapper>
            <List divided verticalAlign="middle">
              {flags.map(flag => {
                return (
                  <List.Item style={{padding: '8px'}}>
                    <List.Content floated="right">
                      <S.DeleteButton>
                        <LegacyWBIcon
                          name="delete"
                          size="large"
                          onClick={() => {
                            const filteredFlags = flags.filter(f => flag !== f);
                            setFlags(filteredFlags);
                          }}
                        />
                      </S.DeleteButton>
                    </List.Content>
                    <S.ContentWrapper>
                      <List.Content>{flag} </List.Content>
                    </S.ContentWrapper>
                  </List.Item>
                );
              })}
            </List>
          </S.FormColumn>
        </S.ClaimsWrapper>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={props.onCloseForm}>Cancel</Button>
        <Button
          primary
          onClick={onSubmitHandler}
          disabled={email === '' || customerName === ''}>
          Generate
        </Button>
      </Modal.Actions>
    </>
  );
};

export default LicenseAdminPage;
